import { useEffect, useState } from 'react';
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { hatPlugin, istNativeApp } from './nativePlatform';
import { probenKappen, type DruckProbe } from './verortung';

/**
 * Anbindung an das Native-Plugin `Standort` der Android-Huelle
 * (android/app/src/main/java/de/gefasmbh/sigeko/StandortPlugin.java,
 * docs/maengel-verortung-plan.md Abschnitt 9.5): Luftdruck, GNSS und Kompass
 * fuer die Sensor-Verortung.
 *
 * Nur in der App und nur mit einer Huelle, die das Plugin kennt (ab 1.0.2) —
 * `standortVerfuegbar()`. Registriert wird erst beim ersten Zugriff (Muster
 * appFlags.ts), im Browser nie.
 *
 * Datenschutz (bindend, Plan Abschnitt 1): Sensoren laufen nur, solange ein
 * Aufrufer `useStandortSensoren(true)` haelt. Die Huelle stoppt sie bei Pause
 * der App und nach 90 s ohne Lebenszeichen selbst. Hier wird nichts
 * gespeichert — nur die letzten Sekunden im Speicher fuer Median und Mittel.
 */

export type StandortErlaubnis = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';

export interface StandortPruefung {
    barometer: boolean;
    barometerName: string | null;
    kompass: 'rotation' | 'magnet' | 'keiner';
    gps: boolean;
    gpsAn: boolean;
    erlaubnis: StandortErlaubnis;
    genau: boolean;
}

export interface RichtungEreignis {
    /** Magnetischer Azimut 0–360 (Nord = 0, im Uhrzeigersinn). */
    azimutGrad: number;
    /** 'kamera' = Blickachse der Rueckkamera (Geraet steht), 'oberkante' = Geraet liegt flach. */
    achse: 'kamera' | 'oberkante';
    /** Neigung der Kameraachse, negativ = nach unten. */
    neigungGrad: number;
    /** Android SENSOR_STATUS: 0 unzuverlaessig, 1 niedrig, 2 mittel, 3 hoch. */
    genauigkeit: 0 | 1 | 2 | 3;
    fehlerGrad: number | null;
    deklinationGrad: number | null;
    t: number;
}

export interface PositionEreignis {
    lat: number;
    lng: number;
    genauigkeitM: number;
    /** Hoehe ueber dem WGS84-Ellipsoid (NICHT ueber NHN; in Deutschland ≈ 40–50 m mehr). */
    hoeheM: number | null;
    hoehenGenauigkeitM: number | null;
    satelliten: number | null;
    zweiFrequenz: boolean | null;
    /** Zeit des Fixes (GNSS-Uhr). */
    t: number;
}

/** Position mit Empfangszeit nach der Geraeteuhr — Fenster und Mittelung rechnen damit, nicht mit der GNSS-Zeit. */
export interface EmpfangenePosition extends PositionEreignis {
    empfangen: number;
}

export type GpsZustand = 'an' | 'aus' | 'keine_erlaubnis' | 'nicht_vorhanden';

export interface StatusEreignis {
    gps: GpsZustand;
    laeuft: boolean;
    grund?: string;
}

export interface StandortPlugin {
    pruefen(): Promise<StandortPruefung>;
    erlaubnisAnfragen(): Promise<StandortPruefung>;
    starten(opts?: { druck?: boolean; kompass?: boolean; gps?: boolean }): Promise<void>;
    /** Watchdog der Huelle: ohne Aufruf 90 s → Sensoren aus. */
    lebenszeichen(): Promise<void>;
    stoppen(): Promise<void>;
    addListener(eventName: 'druck', listener: (e: DruckProbe) => void): Promise<PluginListenerHandle>;
    addListener(eventName: 'richtung', listener: (e: RichtungEreignis) => void): Promise<PluginListenerHandle>;
    addListener(eventName: 'position', listener: (e: PositionEreignis) => void): Promise<PluginListenerHandle>;
    addListener(eventName: 'status', listener: (e: StatusEreignis) => void): Promise<PluginListenerHandle>;
}

let plugin: StandortPlugin | null = null;

/** Das Plugin — beim ersten Aufruf registriert, danach immer derselbe Proxy. */
export function standortPlugin(): StandortPlugin {
    if (!plugin) plugin = registerPlugin<StandortPlugin>('Standort');
    return plugin;
}

/** Sensoren abrufbar? Nur in der App UND mit einer Huelle, die das Plugin kennt. */
export function standortVerfuegbar(): boolean {
    return istNativeApp() && hatPlugin('Standort');
}

/** Abstand des Lebenszeichens — deutlich unter den 90 s des Watchdogs. */
export const LEBENSZEICHEN_MS = 30_000;
/** So lange bleiben Positionen im Speicher (Passpunkt-Messung braucht 30 s). */
export const POSITIONEN_HALTEN_MS = 120_000;

// ─── Eingangspruefung ────────────────────────────────────────────────────────
// Die Huelle ist eigener Code, aber eine alte oder kaputte Version darf die
// Anzeige nicht mit NaN fluten. Unbrauchbares wird verworfen, nicht geraten.

const zahl = (w: unknown): number | null => (typeof w === 'number' && Number.isFinite(w) ? w : null);

const objekt = (w: unknown): Record<string, unknown> | null =>
    w !== null && typeof w === 'object' ? (w as Record<string, unknown>) : null;

/** Druckprobe pruefen: 300–1100 hPa (Baugrube bis Hochhaus, mit Reserve). */
export function druckLesen(roh: unknown): DruckProbe | null {
    const o = objekt(roh);
    if (!o) return null;
    const hPa = zahl(o.hPa);
    const t = zahl(o.t);
    if (hPa === null || t === null || hPa < 300 || hPa > 1100) return null;
    return { hPa, t };
}

export function richtungLesen(roh: unknown): RichtungEreignis | null {
    const o = objekt(roh);
    if (!o) return null;
    const azimut = zahl(o.azimutGrad);
    const t = zahl(o.t);
    if (azimut === null || t === null) return null;
    const g = zahl(o.genauigkeit);
    const genauigkeit = (g !== null && g >= 0 && g <= 3 ? Math.round(g) : 0) as 0 | 1 | 2 | 3;
    return {
        azimutGrad: ((azimut % 360) + 360) % 360,
        achse: o.achse === 'oberkante' ? 'oberkante' : 'kamera',
        neigungGrad: zahl(o.neigungGrad) ?? 0,
        genauigkeit,
        fehlerGrad: zahl(o.fehlerGrad),
        deklinationGrad: zahl(o.deklinationGrad),
        t,
    };
}

export function positionLesen(roh: unknown): PositionEreignis | null {
    const o = objekt(roh);
    if (!o) return null;
    const lat = zahl(o.lat);
    const lng = zahl(o.lng);
    const genauigkeitM = zahl(o.genauigkeitM);
    const t = zahl(o.t);
    if (lat === null || lng === null || genauigkeitM === null || t === null) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || genauigkeitM < 0) return null;
    return {
        lat,
        lng,
        genauigkeitM,
        hoeheM: zahl(o.hoeheM),
        hoehenGenauigkeitM: zahl(o.hoehenGenauigkeitM),
        satelliten: zahl(o.satelliten),
        zweiFrequenz: typeof o.zweiFrequenz === 'boolean' ? o.zweiFrequenz : null,
        t,
    };
}

const ERLAUBNISSE: readonly StandortErlaubnis[] = ['granted', 'denied', 'prompt', 'prompt-with-rationale'];
const GPS_ZUSTAENDE: readonly GpsZustand[] = ['an', 'aus', 'keine_erlaubnis', 'nicht_vorhanden'];

export function pruefungLesen(roh: unknown): StandortPruefung | null {
    const o = objekt(roh);
    if (!o) return null;
    const kompass = o.kompass === 'rotation' || o.kompass === 'magnet' ? o.kompass : 'keiner';
    const erlaubnis = ERLAUBNISSE.find((e) => e === o.erlaubnis) ?? 'prompt';
    return {
        barometer: o.barometer === true,
        barometerName: typeof o.barometerName === 'string' && o.barometerName.trim() ? o.barometerName.trim() : null,
        kompass,
        gps: o.gps === true,
        gpsAn: o.gpsAn === true,
        erlaubnis,
        genau: o.genau === true,
    };
}

export function statusLesen(roh: unknown): StatusEreignis | null {
    const o = objekt(roh);
    if (!o) return null;
    const gps = GPS_ZUSTAENDE.find((z) => z === o.gps);
    if (!gps) return null;
    return { gps, laeuft: o.laeuft === true, grund: typeof o.grund === 'string' ? o.grund : undefined };
}

/**
 * GPS-Zustand fuer die Anzeige. Der letzte Status der Huelle gewinnt — er kommt bei jedem
 * Provider-Wechsel und beim Fortsetzen neu; die Pruefung vom Start kann veraltet sein
 * (Codex-Review 27.09.: GPS nachtraeglich eingeschaltet → Anzeige blieb „aus").
 */
export function gpsZustandAnzeige(status: StatusEreignis | null, pruefung: StandortPruefung | null): GpsZustand | null {
    if (status) return status.gps;
    if (!pruefung) return null;
    if (!pruefung.gps) return 'nicht_vorhanden';
    if (pruefung.erlaubnis !== 'granted' || !pruefung.genau) return 'keine_erlaubnis';
    return pruefung.gpsAn ? 'an' : 'aus';
}

/** Positionen der letzten `POSITIONEN_HALTEN_MS` (nach Empfangszeit), neue hinten. */
export function positionenKappen(liste: readonly EmpfangenePosition[], jetzt: number): EmpfangenePosition[] {
    const grenze = jetzt - POSITIONEN_HALTEN_MS;
    return liste.filter((p) => p.empfangen >= grenze && p.empfangen <= jetzt + 5_000);
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface StandortSensoren {
    /** Plugin in dieser App-Version vorhanden? */
    verfuegbar: boolean;
    pruefung: StandortPruefung | null;
    /** Letzter Status der Huelle (null bis zum ersten Ereignis). */
    status: StatusEreignis | null;
    druckProben: DruckProbe[];
    richtung: RichtungEreignis | null;
    positionen: EmpfangenePosition[];
    fehler: string | null;
    /** Standortrecht anfragen (Android-Dialog) und Pruefung auffrischen. */
    erlaubnisAnfragen: () => Promise<void>;
}

const fehlerText = (e: unknown): string => (e instanceof Error && e.message ? e.message : String(e));

// ─── Nutzerzaehlung ──────────────────────────────────────────────────────────
// Die Huelle kennt EINE Messung. Mehrere Oberflaechen koennen sie gleichzeitig
// halten (Messmodus + Kamera-Vorschau, Wizard-Kamera + Einrichtung). Gestoppt
// wird erst, wenn der letzte Nutzer geht — sonst schaltete das Schliessen der
// Kamera dem offenen Messmodus die Sensoren ab.

let nutzerZahl = 0;

/** Nur fuer Tests: Zaehler zuruecksetzen. */
export function _nutzerZahlZuruecksetzen(): void {
    nutzerZahl = 0;
}

/** Aktuelle Zahl der Nutzer (Tests, Fehlersuche). */
export function sensorNutzerZahl(): number {
    return nutzerZahl;
}

/** Stoppt die Sensoren nur, wenn niemand sie mehr haelt. */
function stoppenWennLetzter(p: Pick<StandortPlugin, 'stoppen'>): void {
    if (nutzerZahl === 0) void p.stoppen().catch(() => undefined);
}

/** Ein Nutzer mehr (Hook wird aktiv). */
export function sensorNutzerAnmelden(): void {
    nutzerZahl += 1;
}

/** Ein Nutzer weniger; der letzte stoppt die Sensoren. */
export function sensorNutzerAbmelden(p: Pick<StandortPlugin, 'stoppen'>): void {
    nutzerZahl = Math.max(0, nutzerZahl - 1);
    stoppenWennLetzter(p);
}

/**
 * Sensoren, solange `aktiv` true ist. Beim Ausschalten, Aushaengen oder
 * Seitenwechsel: Listener weg, `stoppen()` — sobald kein anderer Nutzer die
 * Sensoren mehr haelt (Nutzerzaehlung oben). Ereignisse kommen mit bis zu 5/s;
 * gehalten werden nur 60 s Druck und 120 s Positionen.
 */
export function useStandortSensoren(aktiv: boolean): StandortSensoren {
    const [verfuegbar] = useState<boolean>(standortVerfuegbar);
    const [pruefung, setPruefung] = useState<StandortPruefung | null>(null);
    const [status, setStatus] = useState<StatusEreignis | null>(null);
    const [druckProben, setDruckProben] = useState<DruckProbe[]>([]);
    const [richtung, setRichtung] = useState<RichtungEreignis | null>(null);
    const [positionen, setPositionen] = useState<EmpfangenePosition[]>([]);
    const [fehler, setFehler] = useState<string | null>(null);

    useEffect(() => {
        if (!aktiv || !verfuegbar) return undefined;
        const p = standortPlugin();
        let beendet = false;
        sensorNutzerAnmelden();
        const handles: PluginListenerHandle[] = [];
        const merken = (h: PluginListenerHandle) => {
            if (beendet) void h.remove();
            else handles.push(h);
        };

        void (async () => {
            try {
                const pr = pruefungLesen(await p.pruefen());
                if (beendet) return;
                setPruefung(pr);
                setFehler(null);
                merken(await p.addListener('druck', (e) => {
                    const probe = druckLesen(e);
                    if (probe) setDruckProben((alt) => probenKappen([...alt, probe], probe.t));
                }));
                merken(await p.addListener('richtung', (e) => {
                    const r = richtungLesen(e);
                    if (r) setRichtung(r);
                }));
                merken(await p.addListener('position', (e) => {
                    const pos = positionLesen(e);
                    if (!pos) return;
                    const empfangen = Date.now();
                    setPositionen((alt) => positionenKappen([...alt, { ...pos, empfangen }], empfangen));
                }));
                merken(await p.addListener('status', (e) => {
                    const s = statusLesen(e);
                    if (!s) return;
                    setStatus(s);
                    // Zurueck aus den Android-Einstellungen oder dem Erlaubnis-Dialog: Rechte und
                    // GPS-Schalter koennen sich geaendert haben.
                    if (!beendet && (s.grund === 'fortsetzen' || s.grund === 'erlaubnis')) {
                        void p.pruefen().then((roh) => { if (!beendet) setPruefung(pruefungLesen(roh)); }).catch(() => undefined);
                    }
                    // Watchdog schlug zu, obwohl die Seite die Sensoren noch haelt (z. B. gedrosselter
                    // Zeitgeber): einmal neu starten statt still stehende Werte zu zeigen.
                    if (!beendet && !s.laeuft && s.grund === 'watchdog') {
                        void p.starten({ druck: true, kompass: true, gps: true })
                            // Aufraeumen lief waehrend des Neustarts (Codex-Review Runde 2): wieder aus.
                            .then(() => { if (beendet) stoppenWennLetzter(p); })
                            .catch(() => undefined);
                    }
                }));
                if (beendet) return;
                await p.starten({ druck: true, kompass: true, gps: true });
                // Aufraeumen lief waehrend `starten`: dessen `stoppen` kam evtl. zu frueh an.
                if (beendet) stoppenWennLetzter(p);
            } catch (e) {
                if (!beendet) setFehler(`Sensoren nicht startbar: ${fehlerText(e)}`);
            }
        })();

        const takt = window.setInterval(() => {
            void p.lebenszeichen().catch(() => undefined);
        }, LEBENSZEICHEN_MS);

        return () => {
            beendet = true;
            window.clearInterval(takt);
            handles.splice(0).forEach((h) => { void h.remove(); });
            sensorNutzerAbmelden(p);
        };
    }, [aktiv, verfuegbar]);

    const erlaubnisAnfragen = async (): Promise<void> => {
        if (!verfuegbar) return;
        try {
            // Kein eigener Neustart danach (Codex-Review 27.09.): die Huelle schaltet GPS im
            // Erlaubnis-Rueckruf selbst zu, falls die Messung dann noch laeuft. Ein Start von hier
            // koennte nach dem Aufraeumen des Effekts kommen und die Sensoren wieder anwerfen.
            setPruefung(pruefungLesen(await standortPlugin().erlaubnisAnfragen()));
        } catch (e) {
            setFehler(`Standortrecht nicht anfragbar: ${fehlerText(e)}`);
        }
    };

    return { verfuegbar, pruefung, status, druckProben, richtung, positionen, fehler, erlaubnisAnfragen };
}
